import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { FaHome, FaRss, FaBell, FaSearch, FaShoppingCart } from "react-icons/fa";
import { useAuth } from '../context/AuthContext';
import axiosInstance from "../api/axiosInstance";
import Avatar from "./Avatar";

const Header = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [bellOpen, setBellOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [cartCount, setCartCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState({ users: [], recipes: [], tags: [] });
    const [searchOpen, setSearchOpen] = useState(false);
    const bellRef = useRef(null);
    const avatarRef = useRef(null);
    const searchRef = useRef(null);
    const debounceRef = useRef(null);

    const { logout, currentUser } = useAuth();
    const navigate = useNavigate();

    const fetchUnreadCount = async () => {
      try {
        const res = await axiosInstance.get("/api/notifications/unread-count/");
        setUnreadCount(res.data.unread_count);
      } catch (err) {
        console.error("Failed to fetch unread count", err);
      }
    };

    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get("/api/notifications/");
        setNotifications(res.data.results || []);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      }
    };

    const handleMarkAllRead = async () => {
      try {
        await axiosInstance.post("/api/notifications/mark-read/");
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      } catch (err) {
        console.error("Failed to mark notifications as read", err);
      }
    };

    const handleNotificationClick = async (n) => {
      if (!n.is_read) {
        try {
          await axiosInstance.post(`/api/notifications/${n.id}/read/`);
          setUnreadCount((prev) => Math.max(prev - 1, 0));
          setNotifications((prev) =>
            prev.map((notif) => notif.id === n.id ? { ...notif, is_read: true } : notif)
          );
        } catch (err) {
          console.error("Failed to mark notification as read", err);
        }
      }
      setBellOpen(false);
      if (n.notification_type === "follow") {
        navigate(`/profile/${n.sender?.id}`);
      } else if (n.target_type === "feedpost") {
        navigate("/feed");
      } else {
        navigate(`/recipes/${n.target_object_id}`);
      }
    };

    const handleBellClick = () => {
      if (!bellOpen) fetchNotifications();
      setBellOpen(!bellOpen);
      setDropdownOpen(false);
    };

    const fetchCartCount = async () => {
      try {
        const res = await axiosInstance.get("/api/cart/");
        setCartCount(res.data.items?.length || 0);
      } catch (err) {
        // No active cart — count stays 0
      }
    };

    useEffect(() => {
      fetchUnreadCount();
      fetchCartCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }, []);

    useEffect(() => {
      const handleCartChanged = () => fetchCartCount();
      window.addEventListener("cart-changed", handleCartChanged);
      return () => window.removeEventListener("cart-changed", handleCartChanged);
    }, []);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (bellRef.current && !bellRef.current.contains(e.target)) {
          setBellOpen(false);
        }
        if (avatarRef.current && !avatarRef.current.contains(e.target)) {
          setDropdownOpen(false);
        }
        if (searchRef.current && !searchRef.current.contains(e.target)) {
          setSearchOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, []);

    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!value.trim()) {
        setSearchResults({ users: [], recipes: [], tags: [] });
        setSearchOpen(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await axiosInstance.get("/api/search/", { params: { q: value } });
          setSearchResults(res.data);
          setSearchOpen(true);
        } catch (err) {
          console.error("Search failed", err);
        }
      }, 300);
    };

    const handleSearchResultClick = (path) => {
      setSearchOpen(false);
      setSearchQuery("");
      navigate(path);
    };

    const handleLogout = () => {
      logout();
      navigate("/login");
    };

    return (
        <header className="bg-white shadow-md dark:bg-gray-800 dark:shadow-gray-900/50 p-4 flex items-center justify-between sticky top-0 z-50">
            {/*Search bar should be on the left, home and feed button should be center and userprofile on the right */}
          <div className="flex items-center space-x-4 w-1/3">
            <Link to="/recipes" className="text-xl font-bold text-blue-600">
              CartNom
            </Link>
            <div className="relative w-full max-w-xs" ref={searchRef}>
              <div className="flex items-center border rounded-md px-3 py-1 dark:border-gray-600 dark:bg-gray-700">
                <FaSearch className="text-gray-400 mr-2" size={14} />
                <input
                  type="text"
                  placeholder="Search users, recipes..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full outline-none text-sm dark:bg-transparent dark:text-white dark:placeholder-gray-400"
                />
              </div>
              {searchOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700 dark:shadow-gray-900/50 z-50 max-h-96 overflow-y-auto">
                  {searchResults.users.length > 0 && (
                    <>
                      <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Users</p>
                      {searchResults.users.map((user) => (
                        <div
                          key={`user-${user.id}`}
                          onClick={() => handleSearchResultClick(`/profile/${user.id}`)}
                          className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3"
                        >
                          <Avatar user={user} size="sm" />
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-white">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-gray-400">{user.bio || ""}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {searchResults.recipes.length > 0 && (
                    <>
                      <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase border-t dark:border-gray-700">Recipes</p>
                      {searchResults.recipes.map((recipe) => (
                        <div
                          key={`recipe-${recipe.id}`}
                          onClick={() => handleSearchResultClick(`/recipes/${recipe.id}`)}
                          className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3"
                        >
                          {recipe.image ? (
                            <img src={recipe.image} alt="" className="w-8 h-8 rounded object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs">No img</div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-white">{recipe.title}</p>
                            <p className="text-xs text-gray-400">by {recipe.owner?.first_name} {recipe.owner?.last_name}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {searchResults.tags?.length > 0 && (
                    <>
                      <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase border-t dark:border-gray-700">Tags</p>
                      {searchResults.tags.map((tag) => (
                        <div
                          key={`tag-${tag.id}`}
                          onClick={() => handleSearchResultClick(`/recipes?view=discover&tag=${tag.name}`)}
                          className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                        >
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">#{tag.name}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {searchResults.users.length === 0 && searchResults.recipes.length === 0 && (!searchResults.tags || searchResults.tags.length === 0) && (
                    <p className="px-4 py-6 text-sm text-gray-400 text-center">No results found</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <nav className="flex justify-center space-x-8 w-1/3">
            <Link to="/recipes" className="text-gray-700 hover:text-blue-600 dark:text-gray-300">
              <FaHome size={20} />
            </Link>
            <Link to="/feed" className="text-gray-700 hover:text-blue-600 dark:text-gray-300">
              <FaRss size={20} />
            </Link>
          </nav>

          <div className="relative w-1/3 flex justify-end items-center space-x-5">
            <Link to="/cart" className="text-gray-700 hover:text-blue-600 dark:text-gray-300 relative">
              <FaShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>
            <div className="relative" ref={bellRef}>
              <button onClick={handleBellClick} className="text-gray-700 hover:text-blue-600 dark:text-gray-300 relative">
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-2 bg-white border rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700 dark:shadow-gray-900/50 w-80 z-50 max-h-96 overflow-y-auto">
                  <div className="flex justify-between items-center px-4 py-3 border-b dark:border-gray-700">
                    <h3 className="font-semibold text-gray-800 dark:text-white">Notifications</h3>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:underline">
                        Mark all as read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-400 text-center">No notifications yet</p>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className={`px-4 py-3 border-b dark:border-gray-700 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${!n.is_read ? "bg-blue-50 dark:bg-blue-900/30" : ""}`}
                      >
                        <p className="text-sm text-gray-800 dark:text-gray-200">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(n.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="relative" ref={avatarRef}>
              <button onClick={() => setDropdownOpen(!dropdownOpen)}>
                <Avatar user={currentUser} size="sm" />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border rounded shadow-md dark:bg-gray-800 dark:border-gray-700 dark:shadow-gray-900/50 w-40 z-50">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
    );
};

export default Header;
